import { useEffect, useMemo, useRef, useState } from "react";

import {
  faCheck,
  faFile,
  faMinus,
  faTableLayout,
  faWarning,
} from "@fortawesome/pro-regular-svg-icons";
import { times, uniqBy } from "lodash-es";

import { useFiles } from "src/api/filesApi/queries";
import {
  computeToolCallStatus,
  ToolCallAccordionItem,
  type ToolCallStatus,
  type ToolCallWithResult,
} from "src/base-components/agents/ToolCallDisplay";
import { ChatBody } from "src/base-components/Chat/ChatBody";
import { ChatEmptyState } from "src/base-components/Chat/ChatEmptyState";
import { ChatInputArea } from "src/base-components/Chat/ChatInputArea";
import { ChatLoadingIndicator } from "src/base-components/Chat/ChatLoadingIndicator";
import { ChatMessage } from "src/base-components/Chat/ChatMessage";
import { ChatMessageList } from "src/base-components/Chat/ChatMessageList";
import { ChatMessageSkeleton } from "src/base-components/Chat/ChatMessageSkeleton";
import { ChatReferencesProvider } from "src/base-components/Chat/ChatReferences";
import { ChatTextContent } from "src/base-components/Chat/ChatTextContent";
import { ChatToolApproval } from "src/base-components/Chat/ChatToolApproval";
import { ChatUserBubble } from "src/base-components/Chat/ChatUserBubble";
import {
  extractReferences,
  type MentionItems,
  type MentionKinds,
} from "src/base-components/Chat/PromptBox/mentionContent";
import {
  PromptBox,
  type PromptBoxApi,
} from "src/base-components/Chat/PromptBox/PromptBox";
import { ReferencePill } from "src/base-components/Chat/PromptBox/ReferencePill";
import { ChatEmptyStatePrompt } from "src/base-components/Chat/types";
import { normalizeAnimationSpeed } from "src/base-components/Chat/utils";
import { ClampedItems } from "src/base-components/ClampedItems";
import {
  FloatingWindowInternal,
  useFloatingWindowPosition,
} from "src/base-components/FloatingWindow/FloatingWindow";
import { useFloatingWindowsActions } from "src/base-components/FloatingWindow/hooks";
import { FloatingWindowProps } from "src/base-components/FloatingWindow/store";
import { Pill } from "src/base-components/Pill";
import {
  CopilotSessionViewer,
  type SessionOverride,
  useSessionOverride,
} from "src/cases/copilot/CopilotSessionViewer";
import { buildFieldReferenceMention } from "src/cases/copilot/caseCopilotFieldRef";
import {
  LegacyFieldRefChip,
  parseLegacyFieldRef,
} from "src/cases/copilot/legacyFieldRef";
import { useAgentVersionChange } from "src/cases/copilot/useAgentVersionChange";
import type { CopilotChatMessage } from "src/cases/copilot/useCaseCopilotSession";
import {
  getSessionId,
  useCaseCopilotSession,
} from "src/cases/copilot/useCaseCopilotSession";
import {
  useChatScrollAnchor,
  useSetScrollToBottom,
} from "src/cases/copilot/useChatScroll";
import { useCase } from "src/cases/queries/cases";
import { GetFilesEnvFilesGetEnvEnum } from "src/clients/files";
import { CopilotTitle } from "src/copilot/CopilotTitle";
import { TitleHelpContent } from "src/copilot/TitleHelpContent";
import { useCopilotPrewarm } from "src/copilot/useCopilotPrewarm";
import { Callout } from "src/design-system/Callout";
import { usePowertools } from "src/powertools/hooks";
import { CasePageParams } from "src/router/urls";
import { useCurrentUserId } from "src/store/AuthStore";
import { useParamsDecode } from "src/utils/useParamsDecode";

const SUGGESTION_PROMPTS: ChatEmptyStatePrompt[] = [
  {
    label: "What is the source of the funds in question?",
    prompt: "What is the source of the funds in question?",
  },
  {
    label: "Any unusual transactions recently?",
    prompt: "Have there been any unusual transactions recently?",
  },
  {
    label: "Any connections to known criminal entitites?",
    prompt: "Are there any connections to known criminal entitites?",
  },
];

const MENTION_KINDS: MentionKinds = {
  block: { label: "Blocks", icon: faTableLayout },
  file: {
    label: "Files",
    icon: faFile,
    getDescription: (data) =>
      typeof data.category === "string" ? data.category : undefined,
  },
};

const MAX_VISIBLE_REFERENCE_CHIPS = 3;

const ReferenceChipRow: React.FC<{
  legacyRefLabel: string | null;
  references: Record<string, string>[];
}> = ({ legacyRefLabel, references }) => (
  <div className="flex min-w-0 items-center gap-x-1">
    {legacyRefLabel != null && <LegacyFieldRefChip label={legacyRefLabel} />}
    <ClampedItems
      items={references.flatMap(({ label, ...attrs }) =>
        attrs.id && label
          ? [
              <div key={attrs.id} className="flex min-w-0">
                <ReferencePill
                  attrs={attrs}
                  kinds={MENTION_KINDS}
                  label={label}
                  maxWidth
                />
              </div>,
            ]
          : [],
      )}
      maxVisibleItems={MAX_VISIBLE_REFERENCE_CHIPS}
    />
  </div>
);

export const ToolCallCompactRow: React.FC<{
  toolCall: ToolCallWithResult["toolCall"];
  status: ToolCallStatus | null;
}> = ({ toolCall, status }) => (
  <div className="flex items-center justify-between rounded-lg border border-gray-200 px-2 py-1.5">
    <span className="min-w-0 truncate font-inter-medium-12px text-gray-800">
      {toolCall.metadata.display_name}
    </span>
    {status && (
      <Pill size="sm" variant={status === "error" ? "red" : "green"}>
        <Pill.Icon icon={status === "error" ? faWarning : faCheck} />
        <Pill.Text>{status === "error" ? "Failed" : "Successful"}</Pill.Text>
      </Pill>
    )}
  </div>
);

export const ToolCallGroupMessage: React.FC<{
  toolCallsWithResults: ToolCallWithResult[];
}> = ({ toolCallsWithResults }) => {
  const [expanded, setExpanded] = useState(false);
  const isPowerToolsEnabled = usePowertools();
  const count = toolCallsWithResults.length;

  return (
    <li>
      <button
        className="font-inter-normal-12px text-gray-400"
        type="button"
        onClick={() => setExpanded((prev) => !prev)}
      >
        Called {count} {count === 1 ? "tool" : "tools"}
      </button>
      {expanded && (
        <div className="mt-1 space-y-2">
          {toolCallsWithResults.map((tcwr) =>
            isPowerToolsEnabled ? (
              <ToolCallAccordionItem
                key={tcwr.toolCall.id}
                status={computeToolCallStatus(tcwr.toolResult, null)}
                toolCall={tcwr.toolCall}
                toolResult={tcwr.toolResult}
              />
            ) : (
              <ToolCallCompactRow
                key={tcwr.toolCall.id}
                status={computeToolCallStatus(tcwr.toolResult, null)}
                toolCall={tcwr.toolCall}
              />
            ),
          )}
        </div>
      )}
    </li>
  );
};

/** Progressively reveals text via requestAnimationFrame. */
const useTypewriterText = (content: string, enabled: boolean): string => {
  const [charCount, setCharCount] = useState(enabled ? 0 : content.length);
  const contentRef = useRef(content);
  const enabledRef = useRef(enabled);

  if (enabled && (!enabledRef.current || content !== contentRef.current)) {
    setCharCount(0);
  }
  contentRef.current = content;
  enabledRef.current = enabled;

  useEffect(() => {
    if (!enabled) {
      setCharCount(content.length);
      return;
    }

    const duration = normalizeAnimationSpeed(content.length) * 1000;
    const start = performance.now();

    let rafId: number;
    const tick = () => {
      const elapsed = performance.now() - start;
      const progress = Math.min(elapsed / duration, 1);
      setCharCount(Math.round(progress * content.length));
      if (progress < 1) rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [content, enabled]);

  const sliced = content.slice(0, charCount);
  // If mid-table-row, extend to the end of the line to avoid column shifting.
  const lastNl = sliced.lastIndexOf("\n");
  if (sliced.slice(lastNl + 1).startsWith("|")) {
    const rowEnd = content.indexOf("\n", charCount);
    return content.slice(0, rowEnd === -1 ? content.length : rowEnd);
  }
  return sliced;
};

const CopilotMessage: React.FC<{
  message: CopilotChatMessage;
  animate?: boolean;
}> = ({ message, animate = false }) => {
  const rawContent = message.role !== "tool-group" ? message.content : "";
  const displayContent = useTypewriterText(
    rawContent,
    animate && message.role === "assistant",
  );

  if (message.role === "tool-group") {
    return (
      <ToolCallGroupMessage
        toolCallsWithResults={message.toolCallsWithResults}
      />
    );
  }

  if (message.role === "user") {
    const { legacyRefLabel, text } = parseLegacyFieldRef(message.content);
    const references = uniqBy(
      extractReferences(text).filter((attrs) => attrs.id && attrs.label),
      (attrs) => attrs.id,
    );
    return (
      <ChatMessage role="user">
        {(legacyRefLabel != null || references.length > 0) && (
          <div
            className="mb-1 flex max-w-[95%] justify-end gap-y-1"
            data-loc="copilot-message-references"
          >
            <ReferenceChipRow
              legacyRefLabel={legacyRefLabel}
              references={references}
            />
          </div>
        )}
        <ChatUserBubble message={text} />
      </ChatMessage>
    );
  }

  return (
    <ChatMessage role={message.role}>
      <ChatTextContent content={displayContent} delay={0} useMarkdownRenderer />
    </ChatMessage>
  );
};

export const CaseCopilotWindow: React.FC<FloatingWindowProps> = ({
  computePosition,
  id,
  data: windowData,
}) => {
  const { position, windowRef } = useFloatingWindowPosition(computePosition);
  const { close: closeWindow, updateData } = useFloatingWindowsActions();
  const { case_id: caseId } = useParamsDecode<CasePageParams>();
  const userId = useCurrentUserId();
  const { data: caseData } = useCase(caseId);
  const isPowerToolsEnabled = usePowertools();

  const copilotReference = caseData?.data.template_version?.copilot_reference;
  const agentId = copilotReference?.agent_id;
  const agentVersionId = copilotReference?.latest_agent_version_id;
  const environment = caseData?.data.env ?? "sandbox";

  const fileCategories = caseData?.data.file_categories;
  const fileIds = fileCategories?.flatMap((cat) => cat.file_ids ?? []) ?? [];
  const contextFileIds =
    caseData?.data.template_version?.copilot_settings?.context_file_ids ?? [];

  const blocksConfig = caseData?.data.template_version?.case_blocks_configs;
  const files = useFiles(fileIds, environment as GetFilesEnvFilesGetEnvEnum);
  // An empty list hides the mention button and hint in the PromptBox.
  const mentionItems: MentionItems = useMemo(() => {
    const categoryNameByFileId = Object.fromEntries(
      (fileCategories ?? []).flatMap((cat) =>
        (cat.file_ids ?? []).map((fileId) => [fileId, cat.display_name]),
      ),
    );
    return [
      ...Object.entries(blocksConfig ?? {}).map(([blockKey, blockConfig]) => ({
        value: blockConfig.title,
        id: blockKey,
        kind: "block",
      })),
      ...files.map((file) => {
        const category = categoryNameByFileId[file.id];
        return {
          value: file.attributes.name,
          id: file.id,
          kind: "file",
          ...(category ? { category } : {}),
        };
      }),
    ];
  }, [blocksConfig, fileCategories, files]);

  const {
    activeVersionId,
    hasVersionChanged,
    acceptNewVersion,
    dismissChange,
  } = useAgentVersionChange({
    caseId,
    latestVersionId: agentVersionId,
  });

  const {
    viewingOverride,
    setViewingOverride,
    effectiveUserId,
    effectiveAgentVersionId,
    isViewingOtherSession,
  } = useSessionOverride({ currentUserId: userId, activeVersionId });
  useCopilotPrewarm({
    agentId,
    sessionId: getSessionId(userId, caseId, activeVersionId),
    enabled: true,
  });

  const refLabel = windowData?.refLabel as string | undefined;
  const formKind = windowData?.formKind as "task" | "action" | undefined;
  const formKey = windowData?.formKey as string | undefined;
  const fieldKey = windowData?.fieldKey as string | undefined;

  const promptBoxRef = useRef<PromptBoxApi>(null);

  // Syncs the window data (set by the field's "rewrite with copilot" button)
  // into the imperative editor: append the reference, then consume the data.
  useEffect(() => {
    if (
      refLabel == null ||
      (formKind !== "task" && formKind !== "action") ||
      !formKey ||
      !fieldKey
    ) {
      return;
    }
    promptBoxRef.current?.appendMention(
      buildFieldReferenceMention({
        label: refLabel,
        formKind,
        formKey,
        fieldKey,
      }),
    );
    updateData(id, {});
  }, [refLabel, formKind, formKey, fieldKey, id, updateData]);

  const {
    messages,
    isLoading,
    isActive,
    failureMessage,
    sendMessage,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useCaseCopilotSession({
    agentId,
    agentVersionId: effectiveAgentVersionId,
    caseId,
    userId: effectiveUserId,
    environment,
    fileIds,
    contextFileIds,
  });

  // Silently upgrade to the latest agent version when conversation is empty
  useEffect(() => {
    if (
      hasVersionChanged &&
      !isLoading &&
      !isViewingOtherSession &&
      messages.length === 0
    ) {
      acceptNewVersion();
    }
  }, [
    hasVersionChanged,
    isLoading,
    isViewingOtherSession,
    messages.length,
    acceptNewVersion,
  ]);

  const chronologicalMessages = useMemo(
    () => [...messages].reverse(),
    [messages],
  );

  // Non-zero = typewriter animation active. Incremented on each send to re-trigger the rAF effect.
  const sendEpochRef = useRef(0);
  const [animationEpoch, setAnimationEpoch] = useState(0);
  const isAnimating = animationEpoch > 0;

  const lastUserIndex = useMemo(() => {
    if (!isAnimating) return -1;
    return chronologicalMessages.findLastIndex((m) => m.role === "user");
  }, [isAnimating, chronologicalMessages]);

  const shouldAnimate = (msg: CopilotChatMessage, index: number) =>
    isAnimating &&
    msg.role === "assistant" &&
    msg.id != null &&
    index > lastUserIndex;

  const listRef = useRef<HTMLUListElement>(null);

  const { resetScrollToBottom } = useSetScrollToBottom({
    listRef,
    messages,
    isLoading,
  });

  const { activateAnchor, clearAnchor } = useChatScrollAnchor({
    listRef,
    messages: chronologicalMessages,
    isActive,
    failureMessage,
    isFetchingNextPage,
  });

  // Stable ref so the wheel handler can clear the anchor without
  // restarting the effect on every render.
  const clearAnchorRef = useRef(clearAnchor);
  clearAnchorRef.current = clearAnchor;

  // Survives effect re-runs so a wheel-up stays honoured across isActive toggles.
  const pinnedRef = useRef(true);

  // Keep the list pinned to the bottom while the typewriter reveals content.
  // A wheel-up gesture unpins so the user can scroll freely and load older pages.
  useEffect(() => {
    if (animationEpoch === 0) return;
    const list = listRef.current;
    if (!list) return;

    let rafId: number;
    const deadline = isActive ? Infinity : performance.now() + 4_000;

    const onWheel = (e: WheelEvent) => {
      if (e.deltaY < 0) {
        pinnedRef.current = false;
        clearAnchorRef.current();
      }
    };
    list.addEventListener("wheel", onWheel);

    const scroll = () => {
      if (performance.now() > deadline) {
        setAnimationEpoch(0);
        return;
      }
      if (pinnedRef.current) {
        list.scrollTop = list.scrollHeight;
      }
      rafId = requestAnimationFrame(scroll);
    };
    rafId = requestAnimationFrame(scroll);

    return () => {
      cancelAnimationFrame(rafId);
      list.removeEventListener("wheel", onWheel);
    };
  }, [animationEpoch, isActive]);

  const handleSessionOverride = (override: SessionOverride | null) => {
    setViewingOverride(override);
    setAnimationEpoch(0);
    clearAnchor();
    resetScrollToBottom();
  };

  const handleSend = (text: string) => {
    if (isViewingOtherSession) return;
    const trimmed = text.trim();
    if (!trimmed) return;

    activateAnchor();
    pinnedRef.current = true;
    sendEpochRef.current += 1;
    setAnimationEpoch(sendEpochRef.current);
    sendMessage(trimmed);
  };

  const handleReset = () => {
    setAnimationEpoch(0);
    clearAnchor();
  };

  const handleAcceptNewVersion = () => {
    acceptNewVersion();
    handleReset();
  };

  return (
    <FloatingWindowInternal
      ref={windowRef}
      clipOverflow={false}
      closeIcon={faMinus}
      id={id}
      height="h-150"
      helpText={
        <TitleHelpContent description="Ask questions about this case and get help with analysis, actions, and next steps." />
      }
      isPinned={false}
      maximizable={false}
      style={{
        inset: "0px auto auto 0px",
        transform: position,
      }}
      title={<CopilotTitle />}
      titleRight={
        isPowerToolsEnabled ? (
          <CopilotSessionViewer
            currentAgentVersionId={activeVersionId}
            viewingOverride={viewingOverride}
            onSubmit={handleSessionOverride}
          />
        ) : undefined
      }
      draggable
      resizeable
      onClose={() => closeWindow(id)}
    >
      <ChatReferencesProvider items={mentionItems} kinds={MENTION_KINDS}>
        <ChatBody>
          {messages.length === 0 && !isLoading ? (
            <div className="flex-grow p-2.5">
              {isViewingOtherSession ? (
                <div className="flex h-full flex-grow items-center justify-center p-2 font-inter-normal-12px text-gray-500">
                  No messages in this session
                </div>
              ) : (
                <ChatEmptyState
                  prompts={SUGGESTION_PROMPTS}
                  variant="new"
                  onPromptClick={handleSend}
                />
              )}
            </div>
          ) : (
            <ChatMessageList
              ref={listRef}
              pagination={{
                hasNextPage,
                isFetchingNextPage,
                fetchNextPage,
                loadingSkeleton: <ChatMessageSkeleton />,
              }}
            >
              {messages.length === 0 && isLoading
                ? times(3, (i) => <ChatMessageSkeleton key={i} />)
                : chronologicalMessages.map((message, index) => (
                    <CopilotMessage
                      key={message.id}
                      animate={shouldAnimate(message, index)}
                      message={message}
                    />
                  ))}
              {failureMessage && (
                <li className="mt-4 flex-shrink-0">
                  <Callout variant="error">{failureMessage}</Callout>
                </li>
              )}
              {hasVersionChanged && !isViewingOtherSession && (
                <li className="rounded-lg border border-gray-200 p-3">
                  <ChatToolApproval
                    approveLabel="Start new session"
                    dataLoc="agent-version-change-banner"
                    denyLabel="Continue current session"
                    description="The agent has been updated. Would you like to start a new session with the latest version?"
                    onApprove={handleAcceptNewVersion}
                    onDeny={dismissChange}
                  />
                </li>
              )}
              {isActive && (
                <ChatLoadingIndicator
                  mode="loading"
                  dataLoc="copilot-loading-indicator"
                />
              )}
            </ChatMessageList>
          )}
          {/* this is temporary wrapper to compensate paddings for this case. We need to rework component to make it usable without this wrapper */}
          <div className="-mx-2 -mb-3">
            <ChatInputArea>
              {isViewingOtherSession ? (
                <div className="flex items-center justify-center py-3 font-inter-normal-12px text-gray-500">
                  Viewing another user's session
                </div>
              ) : (
                <PromptBox
                  key={activeVersionId ?? "no-version"}
                  ref={promptBoxRef}
                  dataLoc="copilot-prompt"
                  placeholder="Message Copilot..."
                  referenceButtonAriaLabel="Mention a block or file"
                  referenceHint="Use @ to refer to case blocks and files"
                  submitDisabled={isActive}
                  onSubmit={handleSend}
                />
              )}
            </ChatInputArea>
          </div>
        </ChatBody>
      </ChatReferencesProvider>
    </FloatingWindowInternal>
  );
};
